/**
 * AssemblyAI transcription configuration — SINGLE SOURCE OF TRUTH
 *
 * Used by all transcription pipelines:
 * - api/inngest/functions/transcribeRecording.ts (wizard recordings)
 * - api/inngest/functions/transcribeConcatenated.ts (two-pass concatenated pipeline)
 * - api/inngest/functions/transcriptionPipeline.ts (manual file uploads)
 *
 * Features:
 * - Universal-3-Pro model (best accuracy, required for speech_understanding)
 * - Role-based speaker identification (Instructor, Captain, etc.)
 * - Aviation keyterms prompting for domain-specific vocabulary
 *
 * Determinism caveat — verified against https://assemblyai.com/docs/llms.txt on 2026-04-23:
 * AssemblyAI exposes NO inference-time controls for reproducible output — there is no
 * `seed`, `temperature`, `beam_size`, or sampling parameter. Re-transcribing the same
 * audio may produce slightly different text.
 *
 * What IS locked down here:
 *   - speech_models (universal-3-pro / universal-2)
 *   - keyterms_prompt (aviation vocabulary)
 *   - language_code / code-switching thresholds
 *   - speaker identification roles (known_values)
 *
 * What CANNOT be locked:
 *   - model inference variance (every AssemblyAI customer has the same limitation)
 *   - low-confidence utterance transcription (can flip between runs)
 *
 * Mitigation: don't re-transcribe when we don't have to. transcribeRecording.ts and
 * progressiveTranscription.ts short-circuit when a completed session_transcripts row
 * already exists for the (session_id, phase) tuple (pass force_retranscribe=true to override).
 */

import { PILOT_COMPETENCY_CODES } from '../framework/referenceModel';

// ── Speech Models ────────────────────────────────────────────────────────────
export const ASSEMBLYAI_SPEECH_MODELS = ['universal-3-pro'] as const;

// ── Speaker Identification ───────────────────────────────────────────────────
// Role-based speaker identification: AssemblyAI identifies speakers by their
// role in the training session rather than individual identity.
// known_values tells AssemblyAI what roles to expect in the audio.
export const ASSEMBLYAI_SPEAKER_KNOWN_VALUES = [
  'Instructor',
  'Examiner',
  'Check Airman',
  'Captain',
  'First Officer',
  'Trainee',
  'LOSA Observer',
  'Observer',
  'Voice Alert',
] as const;

export type AssemblyAISpeakerRole = (typeof ASSEMBLYAI_SPEAKER_KNOWN_VALUES)[number] | 'Unknown';

// ── Cockpit Language Options ────────────────────────────────────────────────
// Second language for code switching (English is always the primary language).
// Used by the UI dropdown and backend to set `expected_languages` per-request.
//
// Group 1: U3 Pro native — single-pass transcription (full prompt support)
// Group 2: Non-native — triggers two-pass pipeline (U3 Pro speakers + U2 text)
export const COCKPIT_LANGUAGE_OPTIONS = [
  { code: null, label: 'None (English only)' },

  // U3 Pro native languages (single-pass)
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },

  // Non-native languages (two-pass pipeline)
  { code: 'nl', label: 'Dutch' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'zh', label: 'Mandarin' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ru', label: 'Russian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'pl', label: 'Polish' },
  { code: 'sv', label: 'Swedish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'da', label: 'Danish' },
  { code: 'fi', label: 'Finnish' },
  { code: 'el', label: 'Greek' },
  { code: 'ro', label: 'Romanian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'cs', label: 'Czech' },
] as const;

export type CockpitLanguageCode = (typeof COCKPIT_LANGUAGE_OPTIONS)[number]['code'];

// ── Aviation Key Terms (Safety Fallback) ─────────────────────────────────────
// Sent as `keyterms_prompt` ONLY when the DB-driven glossary lookup fails (e.g.
// Postgres unreachable, network blip). The full curated keyterm sets now live
// in the `glossaries` + `glossary_terms` tables (migration 185) and are loaded
// by `loadKeyterms()` from packages/shared/src/glossary/loadKeyterms.ts.
//
// Keep this list intentionally tiny — just the highest-stakes terms that
// matter even when no aircraft-type-specific glossary is available. Same
// `keyterms_prompt` rules apply: ≤1,000 terms on U3-Pro, ≤200 *words* on U2
// (5–50 char per term; the U2 cap is on cumulative word count, not term count).
//
// `keyterms_prompt` and `prompt` are MUTUALLY EXCLUSIVE on U3-Pro.
export const SAFETY_FALLBACK_KEYTERMS = [
  // Takeoff callouts
  'V1', 'V2', 'rotate', 'TOGA', 'positive rate', 'gear up',
  // Aircraft system acronyms common across all types
  'TCAS', 'GPWS', 'EGPWS', 'ILS',
  // Universal GPWS / TCAS aural warnings (multi-word to avoid false positives)
  'wind shear', 'pull up', 'terrain terrain', 'sink rate',
  'traffic traffic', 'clear of conflict',
  // Emergencies
  'mayday', 'pan pan',
  // Crew handover phrases
  'my controls', 'your controls',
] as const;

// ── Inline Prompt Keyterms ───────────────────────────────────────────────────
// Embedded into the AssemblyAI `prompt` string by buildPromptFromScenario.
// Consumed by: api/lib/progressive/prompt-builder.ts
//
// Curation policy (per AssemblyAI support 2026-05-16, Q4):
//   > Including a large number of terms or common terms that are well
//   > represented in the training data could lead to overcorrections and
//   > hallucinations.
// Measured impact: when this list bloated (May 2026 with the full 410-term
// glossary inlined), Pass 1 raw `VOR` mentions on Skywings briefing-sim
// dropped 31 → 13 on identical audio.
//
// Rules for what stays in this list:
//   - DROP common-English aviation tokens AAI already knows from training
//     (ATC verbs like "cleared/taxi", airport words like "runway/gate",
//     single-word system names like "flaps/gear", common crew labels like
//     "captain/first officer", common verbs like "descend/climb/maintain").
//   - KEEP rare aviation acronyms (VOR, TCAS, GPWS, FMS, FCU…).
//   - KEEP multi-word synthetic aural-warning phrases (terrain terrain,
//     pull up, sink rate) — these benefit most from being anchored.
//   - KEEP ICAO-standard pilot callouts (V1, V2, TOGA, hundred above).
//   - KEEP crew handover phrases (my controls / your controls).
//   - KEEP ORCA360 assessment codes — no aviation meaning otherwise.
//   - Per-aircraft manufacturer terms (ECAM/MCDU/FADEC for Airbus, etc.)
//     are loaded DYNAMICALLY per session from the `glossaries` table via
//     `loadManufacturerHighSignalKeyterms`. They are NOT hardcoded here —
//     the session's fleet model drives which manufacturer subset ships.
//   - Known mis-hearings (e.g. "wan" → "One", "flapps" → "flaps") should
//     use `custom_spelling`, not keyterm boosting.
export const INLINE_PROMPT_KEYTERMS = [
  // Navigation acronyms (rare)
  'VOR', 'NDB', 'ILS', 'DME', 'RNAV', 'waypoint', 'flight level',
  // Aircraft system acronyms (rare)
  'TCAS', 'GPWS', 'EGPWS', 'FMS', 'MCP', 'FCU', 'APU',
  'autopilot', 'autothrottle', 'thrust reversers',
  // ICAO standard pilot callouts (rare phrasing)
  'V1', 'rotate', 'V2', 'TOGA', 'LNAV', 'VNAV',
  'positive rate', 'gear up', 'go around',
  'loc alive', 'glideslope alive',
  'hundred above', 'approaching minimums',
  // Crew handover phrases (specific phrasing matters)
  'my controls', 'your controls', 'I have control', 'you have control',
  // GPWS / EGPWS aural warnings (multi-word synthetic phrases)
  'don\'t sink', 'wind shear', 'windshear', 'pull up',
  'terrain terrain', 'terrain ahead', 'too low terrain',
  'too low gear', 'too low flaps', 'sink rate',
  'bank angle', 'caution terrain', 'obstacle obstacle',
  // TCAS II v7.1 aural warnings (multi-word synthetic phrases)
  'traffic traffic', 'monitor vertical speed',
  'increase climb', 'increase descent',
  'adjust vertical speed', 'maintain vertical speed',
  'crossing climb', 'crossing descend', 'level off level off',
  'clear of conflict',
  // Stall / cabin warnings (multi-word)
  'cabin altitude', 'stall stall',
  // Aviation emergency-specific phrasing
  'mayday', 'pan pan', 'fuel emergency', 'medical emergency', 'engine failure',
  // Domain acronym
  'SOP',
  // ORCA360 competency codes (no aviation meaning AAI could know otherwise).
  // Single-sourced from the framework module so the set can no longer drift
  // (the pre-G2-01 literal list went stale once already, missing PRO).
  // The OCR abbreviation lists in api/process-ocr.ts and
  // api/inngest/functions/ocrHandwrittenNote.ts still carry their own copies.
  ...PILOT_COMPETENCY_CODES,
] as const;

// ── Custom Spelling ─────────────────────────────────────────────────────────
// Deterministic post-transcription substitutions for recurring mis-hearings.
// Per AssemblyAI support (Zineb Akki, 2026-05-16, Q6): `custom_spelling` is
// deterministic (5/5 identical-audio runs) and works alongside `prompt` and
// `keyterms_prompt`. Cheaper and safer than promoting these terms into the
// keyterm boost list (which would also dilute attention — see Q4 / Step 4).
//
// Case sensitivity: AssemblyAI matches `from` strings case-sensitively. Include
// capitalised variants explicitly for each entry that has them.
//
// Curation policy:
//   - Ship only patterns we have a real audio repro for. Don't speculate.
//   - Avoid substitutions that change semantic surface form unless they're
//     clear mis-hearings (e.g. NOT `tree → 3` or `niner → 9`, even though
//     they're ICAO-standard, because they change downstream evidence-grep
//     behaviour and need their own audit).
//   - Pre-deploy: grep `api/validate-evidence.ts` and
//     `api/validate-instructor-evidence.ts` for the literal `from` term;
//     zero hits means the substitution is downstream-safe.
//
// Initial set: aviation-specific mis-hearings observed in val transcripts.
// Per-airline overrides (e.g. call-sign confusions) belong in a future
// `airlines.custom_spelling_overrides JSONB` column, NOT here.
export const AVIATION_CUSTOM_SPELLING = [
  { from: ['wan', 'Wan'], to: 'One' },
  { from: ['flapps', 'Flapps', 'flaaps', 'Flaaps'], to: 'flaps' },
] as const;

// ── Full Transcription Config ────────────────────────────────────────────────
// Base configuration shared by all transcription pipelines.
// Individual pipelines may override specific fields (e.g., speaker_options.max).
export const ASSEMBLYAI_TRANSCRIPTION_CONFIG = {
  speech_models: [...ASSEMBLYAI_SPEECH_MODELS],
  // Auto-detect language with code switching support.
  // expected_languages is set per-request based on user's cockpit language selection.
  language_detection: true,
  language_detection_options: {
    code_switching: true,
    code_switching_confidence_threshold: 0.3,
  },
  speaker_labels: true,
  speaker_options: {
    min_speakers_expected: 2,
    max_speakers_expected: 4,
  },
  speech_understanding: {
    request: {
      speaker_identification: {
        speaker_type: 'role' as const,
        known_values: [...ASSEMBLYAI_SPEAKER_KNOWN_VALUES],
      },
      translation: {
        target_languages: ['en'],
        match_original_utterance: true,
      },
    },
  },
  // keyterms_prompt is injected per-request by callers via buildPass1Config /
  // buildPass2Config (which fetch the right subset from the glossaries table
  // via loadKeyterms()). The fallback below is only used when callers don't
  // pass keyterms explicitly — primarily defensive.
  keyterms_prompt: [...SAFETY_FALLBACK_KEYTERMS],
  custom_spelling: AVIATION_CUSTOM_SPELLING.map((e) => ({
    from: [...e.from],
    to: e.to,
  })),
  punctuate: true,
  format_text: true,
} as const;

// ── Manual Upload Pipeline Config ────────────────────────────────────────────
// For the manual file upload pipeline (transcriptionPipeline.ts),
// which handles multi-speaker ATC/cockpit audio and may have more speakers.
export const ASSEMBLYAI_MANUAL_UPLOAD_CONFIG = {
  ...ASSEMBLYAI_TRANSCRIPTION_CONFIG,
  speaker_options: {
    min_speakers_expected: 2,
    max_speakers_expected: 15, // ATC audio can have many speakers
  },
} as const;

// ── Speaker Identification: speakers[] builder (descriptions-based path) ────
//
// Alternative to the `known_values` + free-form `prompt` approach. AssemblyAI's
// `speech_understanding.request.speaker_identification.speakers[]` accepts
// per-role descriptions that *steer* speaker classification (docs:
// https://www.assemblyai.com/docs/speech-understanding/speaker-identification —
// "A description of the speaker to help the model identify them based on
// conversational context."). Using this surface frees `keyterms_prompt` back up
// for the dedicated boost mechanism instead of cramming the role definitions
// AND the aviation glossary into a single `prompt` string.
//
// When this builder is used, the caller MUST drop `known_values` from the
// speaker_identification config — the two are mutually exclusive per docs.

export interface VoicePassportSample {
  /** Role label exactly as it appears in scenario.knownValues (e.g. "Captain"). */
  role: string;
  /** A representative utterance from a prior phase. Optional. */
  sampleUtterance?: string | null;
}

export interface SpeakerEntry {
  role: string;
  description: string;
}

/**
 * Build the `speakers[]` payload for `speech_understanding.request.speaker_identification`.
 *
 * - One entry per role in `knownValues`.
 * - `description` is the per-role text from the resolved scenario.
 * - If a `voicePassportSample` for that role is supplied, its sample utterance
 *   is appended to the description as `Sample utterance from prior phase: "..."`.
 *   The Voice Alert role is skipped for sample appending (synthetic system,
 *   no human-style sample applies).
 *
 * Per docs, `speakers[]` is mutually exclusive with `known_values` — the caller
 * is responsible for not setting both.
 */
export function buildSpeakers(
  knownValues: readonly string[],
  speakerRoleDescriptions: Readonly<Record<string, string>>,
  voicePassportSamples: readonly VoicePassportSample[] = [],
): SpeakerEntry[] {
  const samplesByRole = new Map<string, string>();
  for (const s of voicePassportSamples) {
    if (s.sampleUtterance && s.sampleUtterance.trim().length > 0) {
      samplesByRole.set(s.role.toLowerCase(), s.sampleUtterance.trim());
    }
  }

  return knownValues.map((role) => {
    const baseDescription = speakerRoleDescriptions[role] ?? '';
    const sample =
      role.toLowerCase() === 'voice alert'
        ? undefined
        : samplesByRole.get(role.toLowerCase());

    const description = sample
      ? `${baseDescription} Sample utterance from prior phase: "${sample}"`.trim()
      : baseDescription;

    return { role, description };
  });
}

/**
 * Pass 1 config variant that uses `speakers[]` + `keyterms_prompt` (no `prompt`).
 *
 * Use this when the env flag `ASSEMBLYAI_USE_SPEAKERS_DESCRIPTIONS` is on.
 * Role context lives in `speakers[].description`; aviation vocabulary lives in
 * `keyterms_prompt`. This avoids the prompt-vs-keyterms_prompt trade-off and
 * keeps role definitions from being diluted by the glossary list.
 *
 * Returns a config object ready to be merged into the request body. The caller
 * supplies `audio_url` and optional webhook fields.
 */
export function buildPass1ConfigWithSpeakers(
  scenario: {
    knownValues: readonly string[];
    speakerRoleDescriptions: Readonly<Record<string, string>>;
    minSpeakers?: number;
    maxSpeakers?: number;
  },
  keyterms?: readonly string[],
  voicePassportSamples: readonly VoicePassportSample[] = [],
  /**
   * Scenario-context prompt: the rendered scenario template with role / snippet
   * / keyterm slots stripped. Carries phase context, cockpit-audio expectations,
   * transcription instructions, and scenario description. Optional — when not
   * supplied the request omits `prompt` entirely.
   */
  scenarioContextPrompt?: string | null,
) {
  const speakers = buildSpeakers(
    scenario.knownValues,
    scenario.speakerRoleDescriptions,
    voicePassportSamples,
  );

  // max_speakers_expected must be at least the number of speakers we list,
  // otherwise AssemblyAI silently caps role assignment and Voice Alert (the
  // synthetic callout role) is the most likely casualty. The resolver already
  // filters knownValues down to the session's actual crew composition, so
  // speakers.length is the right floor here.
  const maxSpeakersExpected = Math.max(scenario.maxSpeakers ?? 4, speakers.length);

  const base: Record<string, unknown> = {
    speech_models: ['universal-3-pro'] as const,
    language_code: 'en',
    speaker_labels: true,
    speaker_options: {
      min_speakers_expected: scenario.minSpeakers ?? 2,
      max_speakers_expected: maxSpeakersExpected,
    },
    speech_understanding: {
      request: {
        speaker_identification: {
          speaker_type: 'role' as const,
          speakers,
        },
      },
    },
    keyterms_prompt:
      keyterms && keyterms.length > 0 ? [...keyterms] : [...SAFETY_FALLBACK_KEYTERMS],
    custom_spelling: AVIATION_CUSTOM_SPELLING.map((e) => ({
      from: [...e.from],
      to: e.to,
    })),
  };

  if (scenarioContextPrompt && scenarioContextPrompt.trim().length > 0) {
    base.prompt = scenarioContextPrompt;
  }

  return base;
}

// ── Multi-Pass Transcription Config Builders ────────────────────────────────
// Used for regions whose secondary language is NOT natively supported by U3 Pro.
// Pass 1: Speaker labels (U3 Pro locked, English forced, full ORCA prompt)
// Pass 2: Multilingual text (U2, code-switching + translation to English)
// Single-pass: U3 native languages use one call with full prompt + language_codes.

/**
 * Pass 1: Speaker labeling config — U3 Pro locked, English forced.
 * Used for BOTH single-pass (U3 native) AND two-pass Pass 1.
 * Uses speech_models array (singular `speech_model` is deprecated).
 *
 * Includes speech_understanding.speaker_identification so AssemblyAI
 * returns role-based speaker labels (Captain, First Officer, etc.)
 * in the response mapping. Also includes speaker_options and
 * keyterms_prompt (when no prompt is provided).
 */
export function buildPass1Config(
  prompt: string,
  scenarioOptions?: {
    knownValues?: string[];
    minSpeakers?: number;
    maxSpeakers?: number;
  },
  /**
   * Per-session curated keyterms (typically loaded from glossaries via
   * loadKeyterms()). Capped at 1000 by the caller. If omitted and no
   * prompt is supplied, falls back to SAFETY_FALLBACK_KEYTERMS so a DB
   * read failure doesn't strip all aviation hints.
   */
  keyterms?: readonly string[],
) {
  const knownValues = scenarioOptions?.knownValues ?? [...ASSEMBLYAI_SPEAKER_KNOWN_VALUES];
  const config: Record<string, unknown> = {
    speech_models: ['universal-3-pro'] as const,
    language_code: 'en',
    speaker_labels: true,
    speaker_options: {
      min_speakers_expected: scenarioOptions?.minSpeakers ?? 2,
      // Use the actual scenario crew count (= knownValues.length, which
      // already includes Voice Alert) as the upper bound. A loose cap forces
      // AAI to under-cluster on rapid-fire crew dialogue, merging adjacent
      // voices into a single utterance with one dominant speaker.
      max_speakers_expected: Math.max(
        scenarioOptions?.maxSpeakers ?? 4,
        knownValues.length,
      ),
    },
    speech_understanding: {
      request: {
        speaker_identification: {
          speaker_type: 'role' as const,
          known_values: knownValues,
        },
      },
    },
  };

  // prompt and keyterms_prompt are mutually exclusive on U3-Pro
  if (prompt) {
    config.prompt = prompt;
  } else {
    config.keyterms_prompt = keyterms && keyterms.length > 0
      ? [...keyterms]
      : [...SAFETY_FALLBACK_KEYTERMS];
  }

  // custom_spelling works alongside both prompt and keyterms_prompt
  // (AAI support 2026-05-16, Q6 — deterministic across runs).
  config.custom_spelling = AVIATION_CUSTOM_SPELLING.map((e) => ({
    from: [...e.from],
    to: e.to,
  }));

  return config;
}

/**
 * Single-pass extension for U3 native languages (en, fr, es, pt, de, it).
 * Adds language_codes on top of Pass 1 config for code-switching support.
 */
export function buildSinglePassConfig(
  prompt: string,
  secondaryLangCode: string | null,
) {
  // language_code (singular) and language_codes (plural) are different params.
  // language_codes enables code-switching between listed languages.
  // When language_codes is set, language_code and language_detection must be omitted.
  const { language_code: _drop, ...base } = buildPass1Config(prompt);
  const hasSecondary = secondaryLangCode && secondaryLangCode !== 'en';
  return {
    ...base,
    ...(hasSecondary
      ? { language_codes: ['en', secondaryLangCode] }
      : { language_code: 'en', language_detection: true }),
  };
}

/**
 * Pass 2: Multilingual text — Universal-2 only, code-switching detection.
 * Only used for non-native languages (nl, ar, zh, etc.) that U3 Pro
 * cannot handle. U2 supports all 99 languages including English.
 *
 * Key settings:
 * - `speech_models: ['universal-2']` — U2 only. U3 Pro is excluded because
 *   it cannot transcribe the secondary language (that's why we need Pass 2).
 * - `expected_languages: ['en', secondaryLangCode]` — constrains auto-detection
 *   to the expected languages. Uses language_detection_options (not top-level
 *   language_codes) per Zineb's recommendation.
 * - `speaker_labels: true` — required for speaker_identification to work.
 *   Merge still uses Pass 1 speakers (U3 Pro + prompt) as primary source.
 * - `keyterms_prompt` — supported by U2 in beta (English-only boost). Caller
 *   passes the U2-eligible subset (5–50 chars, capped at 200 *words* total
 *   — the AssemblyAI limit is on cumulative word count, not term count).
 *   The `prompt` field is NOT supported on U2 and is therefore omitted.
 * - `speech_understanding.translation` with `match_original_utterance: true`
 *   provides per-utterance English translations (used by merge to replace
 *   non-English segments from Pass 1).
 * - `speech_understanding.speaker_identification` with role-based known_values
 *   gives U2 some speaker role inference ability, improving merge quality.
 */
export function buildPass2Config(
  expectedLangsOrSecondary: string[] | string | null | undefined,
  scenarioOptions?: {
    knownValues?: string[];
    minSpeakers?: number;
    maxSpeakers?: number;
  },
  /**
   * U2-eligible keyterms (typically loaded from glossaries via
   * loadKeyterms(…, { model: 'u2' })). Caller has already filtered for
   * U2's 5–50 char per-term limit and packed the list to fit AssemblyAI's
   * 200-*word* cap on keyterms_prompt (not 200 terms — multi-word phrases
   * count for their word length). When omitted, falls back to
   * SAFETY_FALLBACK_KEYTERMS (~17 critical terms).
   */
  keyterms?: readonly string[],
) {
  // Accept either a full expected_languages array (from airlines table)
  // or a single secondary language code (legacy fallback)
  let expectedLanguages: string[];
  if (Array.isArray(expectedLangsOrSecondary) && expectedLangsOrSecondary.length > 0) {
    // Airline-level expected_languages — use as-is (already includes 'en')
    expectedLanguages = expectedLangsOrSecondary;
  } else if (
    typeof expectedLangsOrSecondary === 'string' &&
    expectedLangsOrSecondary !== 'en'
  ) {
    // Single secondary language code — legacy path
    expectedLanguages = ['en', expectedLangsOrSecondary];
  } else {
    expectedLanguages = ['en'];
  }

  const knownValues = scenarioOptions?.knownValues ?? [...ASSEMBLYAI_SPEAKER_KNOWN_VALUES];

  return {
    speech_models: ['universal-2'] as const,
    language_detection: true,
    language_detection_options: {
      code_switching: true,
      expected_languages: expectedLanguages,
    },
    speaker_labels: true,
    speaker_options: {
      min_speakers_expected: scenarioOptions?.minSpeakers ?? 2,
      // Same logic as Pass 1: use the scenario's actual crew count as the
      // floor so AAI doesn't under-cluster on rapid-fire crew dialogue.
      max_speakers_expected: Math.max(
        scenarioOptions?.maxSpeakers ?? 6,
        knownValues.length,
      ),
    },
    keyterms_prompt:
      keyterms && keyterms.length > 0
        ? [...keyterms]
        : [...SAFETY_FALLBACK_KEYTERMS],
    custom_spelling: AVIATION_CUSTOM_SPELLING.map((e) => ({
      from: [...e.from],
      to: e.to,
    })),
    speech_understanding: {
      request: {
        speaker_identification: {
          speaker_type: 'role' as const,
          known_values: knownValues,
        },
        translation: {
          target_languages: ['en'],
          match_original_utterance: true,
        },
      },
    },
  };
}

